import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { IconAlertOn, IconBack, IconChat, IconFilter, IconNext, IconShield, IconVerified, IconX } from "../ui/icons";
import { AsyncButton, Empty, ErrorState, Grade, GradeGuide, ListingCard, Notice, useToast, type ListingSummary } from "../ui/kit";
import { useAsync } from "../lib/useAsync";
import { useAppConfig } from "../lib/appConfig";
import { HomeBanners } from "../ui/Banners";
import { hasSeenWelcome } from "../lib/firstVisit";
import { useRequireAuth, useSession } from "../lib/session";

// Tabs come from the catalog staff manage in the console: what exists, its names and its order.
type Named = { labelEn: string; labelAr: string };
type SeriesFacet = Named & { series: string; models: (Named & { model: string })[] };
type Facets = {
  categories?: (Named & { key: string })[];
  brands: (Named & { brand: string })[];
  storages: { gb: number; count: number }[];
  colors?: { name: string; count: number; ar: string | null; hex: string | null }[];
  /** Brand → series ("iPhone 16") → models ("iPhone 16 Pro Max"). */
  models?: { brand: string; series: SeriesFacet[] }[];
};
type SearchResponse = {
  filters: { terms: string[]; category: string | null; brands: string[]; storages: number[]; maxPriceCents: number | null; minPriceCents: number | null; minCondition: string | null; quantity: number; delivery: string; city: string | null };
  facets: Facets;
  total: number;
  notice: string | null;
  results: (ListingSummary & { productId: string })[];
};
type Patch = Record<string, string | null>;

const PAGE = 48;
const SAR = 100;
const CONDITION_STEPS = ["new", "like_new", "good", "fair"] as const;
const PRICE_RANGES: [number | null, number | null][] = [[null, 1000], [1000, 2000], [2000, 3500], [3500, null]];
const list = (v: string | null) => (v ? v.split(",").filter(Boolean) : []);

/**
 * The front door: someone opening walashy.com for the first time on this device, not signed in, sees the welcome
 * page once. Links into the store (a search, a listing, a brand) always open directly.
 */
export function Home() {
  const { me, loading } = useSession();
  const [params] = useSearchParams();
  const firstTime = params.toString() === "" && !hasSeenWelcome();
  if (firstTime && loading) return null; // a moment while we learn whether they're signed in
  if (firstTime && !me) return <Navigate to="/welcome" replace />;
  return <Browse />;
}

export function Browse() {
  const { t, money, locale } = useI18n();
  const { currency } = useAppConfig();
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const home = query === "";
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setLimit(PAGE), [query]);
  useEffect(() => {
    document.title = `${t("browse.title")} · Walashy`;
  }, [t]);

  const { data, error, loading, reload } = useAsync(() => api<SearchResponse>(`/api/catalog/search?${query}${query ? "&" : ""}limit=${limit}`), [query, limit]);

  const set = (patch: Patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: false });
  };
  const toggleStorage = (value: string) => {
    const cur = list(params.get("storage"));
    set({ storage: (cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]).join(",") || null });
  };

  const f = data?.filters;
  const brands = list(params.get("brand"));
  const series = list(params.get("series"));
  const models = list(params.get("model"));
  const storages = list(params.get("storage"));
  const colorsOn = list(params.get("color"));
  const label = (n: Named) => (locale === "ar" ? n.labelAr : n.labelEn);

  // Every filter in force, including those read from the search text, each removable on its own.
  // Removing one turns the rest of the interpretation into explicit filters, so it isn't re-read from the text.
  const chips: { key: string; label: string; clear: Patch }[] = [];
  for (const s of storages) chips.push({ key: `s-${s}`, label: `${s} GB`, clear: { storage: storages.filter((x) => x !== s).join(",") || null } });
  for (const c of colorsOn) {
    const info = data?.facets.colors?.find((x) => x.name.toLowerCase() === c.toLowerCase());
    chips.push({ key: `c-${c}`, label: (locale === "ar" && info?.ar) || c, clear: { color: colorsOn.filter((x) => x !== c).join(",") || null } });
  }
  if (params.get("warranty")) chips.push({ key: "w", label: t("filters.warranty"), clear: { warranty: null } });
  if (f?.maxPriceCents) chips.push({ key: "max", label: t("browse.upTo", { p: money(f.maxPriceCents, currency) }), clear: { maxPrice: null } });
  if (f?.minPriceCents) chips.push({ key: "min", label: t("browse.from", { p: money(f.minPriceCents, currency) }), clear: { minPrice: null } });
  if (f?.minCondition) chips.push({ key: "cond", label: t("cond.atLeast", { c: t(`cond.${f.minCondition}` as MessageKey) }), clear: { minCondition: null } });
  if (f && f.quantity > 1) chips.push({ key: "qty", label: t("browse.qty", { n: f.quantity }), clear: { quantity: null } });
  if (f?.delivery === "delivery") chips.push({ key: "del", label: t("browse.delivery"), clear: { delivery: null } });
  if (f?.delivery === "pickup") chips.push({ key: "pick", label: t("browse.pickup"), clear: { delivery: null } });
  const removeChip = (c: (typeof chips)[number]) => {
    if (!f) return set(c.clear);
    set({
      q: f.terms.join(" ") || null,
      maxPrice: f.maxPriceCents ? String(f.maxPriceCents) : null,
      minPrice: f.minPriceCents ? String(f.minPriceCents) : null,
      minCondition: f.minCondition,
      quantity: f.quantity > 1 ? String(f.quantity) : null,
      delivery: f.delivery === "any" ? null : f.delivery,
      ...c.clear,
    });
  };
  // Clearing filters keeps the category and the sort: they're where you are, not what you narrowed.
  const clearAll = () => {
    const keep = new URLSearchParams();
    for (const k of ["category", "sort"]) if (params.get(k)) keep.set(k, params.get(k)!);
    setParams(keep);
  };

  const sheet = useRef<HTMLDialogElement>(null);
  const sheetTitle = useId();
  const sort = params.get("sort") ?? "best";
  const q = params.get("q");
  const facets: Facets = data?.facets ?? { brands: [], storages: [] };
  const category = data?.filters.category ?? params.get("category");
  // Model rows appear under the brand tabs once one brand is chosen, then its versions once one series is.
  const brandSeries = brands.length === 1 ? (facets.models?.find((b) => b.brand.toLowerCase() === brands[0]!.toLowerCase())?.series ?? []) : [];
  const openSeries = series.length === 1 ? brandSeries.find((s) => s.series === series[0]) : undefined;
  const shown = data?.results.length ?? 0;
  const total = data?.total ?? shown;

  const filters = <Filters facets={facets} params={params} set={set} toggleStorage={toggleStorage} onClear={clearAll} activeCount={chips.length} />;

  return (
    <div>
      <h1 className="sr-only">{t("browse.title")}</h1>
      {home && <HomeBanners />}
      {(facets.categories?.length ?? 0) > 1 && (
        <nav className="tabs" aria-label={t("filters.category")}>
          {facets.categories!.map((c) => (
            <button key={c.key} aria-pressed={category === c.key} onClick={() => setParams(new URLSearchParams({ category: c.key }))}>{label(c)}</button>
          ))}
        </nav>
      )}
      <nav className="tabs" aria-label={t("filters.brand")}>
        <button aria-pressed={brands.length === 0} onClick={() => set({ brand: null, series: null, model: null })}>{t("browse.all")}</button>
        {facets.brands.map((b) => {
          const only = brands.length === 1 && brands[0]!.toLowerCase() === b.brand.toLowerCase();
          return (
            <button key={b.brand} aria-pressed={only} onClick={() => set({ brand: only ? null : b.brand, series: null, model: null })}>{label(b)}</button>
          );
        })}
      </nav>
      {brandSeries.length > 0 && (
        <nav className="tabs" aria-label={t("filters.model")}>
          <button aria-pressed={series.length === 0 && models.length === 0} onClick={() => set({ series: null, model: null })}>
            {t("browse.allModels")}
          </button>
          {brandSeries.map((s) => {
            const only = series.length === 1 && series[0] === s.series;
            return (
              <button key={s.series} aria-pressed={only} onClick={() => set({ series: only ? null : s.series, model: null })}>
                <bdi>{label(s)}</bdi>
              </button>
            );
          })}
        </nav>
      )}
      {openSeries && openSeries.models.length > 1 && (
        <nav className="tabs" aria-label={t("filters.version")}>
          <button aria-pressed={models.length === 0} onClick={() => set({ model: null })}>
            {t("browse.allOf", { s: label(openSeries) })}
          </button>
          {openSeries.models.map((m) => {
            const only = models.length === 1 && models[0] === m.model;
            return (
              <button key={m.model} aria-pressed={only} onClick={() => set({ model: only ? null : m.model })}>
                <bdi>{label(m)}</bdi>
              </button>
            );
          })}
        </nav>
      )}
      {home && (
        <div className="trust-line">
          <span><IconShield size={16} /> {t("trust.free")}</span>
          <span><IconChat size={16} /> {t("trust.direct")}</span>
          <span><IconVerified size={16} /> {t("trust.tracked")}</span>
          <Link to="/welcome" className="link">{t("welcome.learnMore")}</Link>
        </div>
      )}

      {home && <DealsRow />}

      <section className="catalog" aria-labelledby="catalog-title" style={{ marginTop: home ? "var(--s7)" : "var(--s5)" }}>
        <aside className="filters-panel" aria-label={t("browse.filters")}>{filters}</aside>

        <div>
          <div className="results-bar">
            <h2 id="catalog-title" dir="auto">{q ? t("catalog.for", { q }) : t(category === "laptop" ? "catalog.heading.laptop" : "catalog.heading")}</h2>
            {data && <span className="count" aria-live="polite">{t(category === "laptop" ? "catalog.count.laptop" : "catalog.count", { n: total })}</span>}
            <span className="spacer" />
            {!home && <SaveSearch params={params} label={q || chips.map((c) => c.label).join(" · ") || t("browse.all")} />}
            <button className="btn sm filter-btn" onClick={() => sheet.current?.showModal()} aria-haspopup="dialog">
              <IconFilter size={18} /> {chips.length ? t("filters.active", { n: chips.length }) : t("filters.open")}
            </button>
            <label className="sort-select">
              <span className="sr-only">{t("sort.label")}</span>
              <select className="input" value={sort} onChange={(e) => set({ sort: e.target.value === "best" ? null : e.target.value })}>
                {(["best", "price", "savings", "newest"] as const).map((s) => (
                  <option key={s} value={s}>{t(`browse.sort.${s}`)}</option>
                ))}
              </select>
            </label>
          </div>

          {chips.length > 0 && (
            <div className="chips active-filters" aria-label={t("browse.filters")}>
              {chips.map((c) => (
                <button key={c.key} className="chip removable" onClick={() => removeChip(c)} aria-label={t("browse.removeFilter", { f: c.label })}>
                  <span dir="auto">{c.label}</span> <IconX size={14} />
                </button>
              ))}
              {chips.length > 1 && <button className="link" onClick={clearAll}>{t("filters.clear")}</button>}
            </div>
          )}

          {data?.notice === "laptops_soon" && <Notice>{t("browse.laptopsSoon")}</Notice>}

          {loading && !data ? (
            <div className="grid" aria-busy="true">
              <span className="sr-only">{t("state.loading")}</span>
              {Array.from({ length: 8 }, (_, i) => <div key={i} className="skeleton" style={{ aspectRatio: "3 / 4.2" }} />)}
            </div>
          ) : error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : data && data.results.length === 0 ? (
            data.notice ? null : (
              <Empty
                title={t("browse.empty.title")}
                body={t("browse.empty.body")}
                action={
                  <div className="row wrap" style={{ justifyContent: "center" }}>
                    {chips.length > 0 && <button className="btn" onClick={clearAll}>{t("filters.clear")}</button>}
                    <SaveSearch params={params} label={q || chips.map((c) => c.label).join(" · ") || t("browse.all")} primary />
                  </div>
                }
              />
            )
          ) : (
            <>
              <div className="grid">
                {data!.results.map((l, i) => <ListingCard key={l.id} l={l} index={i} />)}
              </div>
              {total > shown ? (
                <div className="show-more">
                  <span>{t("catalog.showing", { n: shown, total })}</span>
                  <span className="bar" aria-hidden><span style={{ width: `${(shown / total) * 100}%` }} /></span>
                  <button className="btn" onClick={() => setLimit((n) => n + PAGE)} disabled={loading}>{t("catalog.more")}</button>
                </div>
              ) : null}

            </>
          )}
        </div>
      </section>

      <dialog ref={sheet} className="sheet" aria-labelledby={sheetTitle}>
        <div className="sheet-head">
          <button className="icon-btn" onClick={() => sheet.current?.close()} aria-label={t("common.close")}><IconX /></button>
          <h2 id={sheetTitle}>{t("browse.filters")}</h2>
        </div>
        <div className="sheet-body">{filters}</div>
        <div className="sheet-foot">
          {chips.length > 0 ? <button className="link" onClick={clearAll}>{t("filters.clear")}</button> : <span />}
          <button className="btn primary" onClick={() => sheet.current?.close()}>{t("filters.show", { n: total })}</button>
        </div>
      </dialog>
    </div>
  );
}

function Section({ title, hint, action, children }: { title: string; hint?: string; action?: ReactNode; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id}>
      <div className="section-head">
        <div>
          <h2 id={id}>{title}</h2>
          {hint && <p>{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Biggest savings against the new price. Swipe on phones; arrow buttons for mouse and keyboard users. */
function DealsRow() {
  const { t, dir } = useI18n();
  const row = useRef<HTMLDivElement>(null);
  const { data } = useAsync(() => api<SearchResponse>("/api/catalog/search?sort=savings&limit=12"), []);
  const deals = data?.results.filter((l) => l.savingsPct) ?? [];
  if (!deals.length) return null;
  // "Next" moves toward the end of the reading direction: left in Arabic, right in English.
  const scroll = (forward: boolean) => {
    const el = row.current;
    if (!el) return;
    const step = el.clientWidth * 0.8 * (forward ? 1 : -1) * (dir === "rtl" ? -1 : 1);
    el.scrollBy({ left: step, behavior: "smooth" });
  };
  return (
    <Section
      title={t("home.deals")}
      hint={t("home.dealsHint")}
      action={
        <div className="actions">
          <Link className="link" to="/?sort=savings">{t("home.seeAll")}</Link>
          <span className="row-nav">
            <button onClick={() => scroll(false)} aria-label={t("row.prev")}><IconBack size={18} /></button>
            <button onClick={() => scroll(true)} aria-label={t("row.next")}><IconNext size={18} /></button>
          </span>
        </div>
      }
    >
      <div className="scroll-row" ref={row}>
        {deals.map((l, i) => <ListingCard key={l.id} l={l} index={i} compact />)}
      </div>
    </Section>
  );
}

const SAVED_KEYS = ["q", "category", "brand", "series", "model", "storage", "maxPrice", "minPrice", "minCondition", "delivery", "city"] as const;

/** "Alert me": keeps this search and tells the buyer the moment a matching phone is listed. */
function SaveSearch({ params, label, primary }: { params: URLSearchParams; label: string; primary?: boolean }) {
  const { t } = useI18n();
  const toast = useToast();
  const requireAuth = useRequireAuth();
  const [saved, setSaved] = useState(false);
  useEffect(() => setSaved(false), [params]);
  const query = Object.fromEntries(SAVED_KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)!]));
  if (!Object.keys(query).length) return null;
  return (
    <AsyncButton
      className={`btn sm ${primary ? "primary" : ""}`}
      disabled={saved}
      onClick={async () => {
        if (!requireAuth()) return;
        try {
          await api("/api/saved-searches", { body: { label: label.slice(0, 80), query } });
          setSaved(true);
          toast(t("browse.alertOn"));
        } catch (e) {
          toast((e as ApiError).message);
        }
      }}
    >
      <IconAlertOn size={16} /> {saved ? t("browse.alertSaved") : t("browse.alertMe")}
    </AsyncButton>
  );
}

function Filters({ facets, params, set, toggleStorage, onClear, activeCount }: { facets: Facets; params: URLSearchParams; set: (p: Patch) => void; toggleStorage: (v: string) => void; onClear: () => void; activeCount: number }) {
  const { t, money, locale } = useI18n();
  const { currency } = useAppConfig();
  const storages = list(params.get("storage"));
  const minCondition = params.get("minCondition");
  const delivery = params.get("delivery") ?? "any";
  const minP = params.get("minPrice");
  const maxP = params.get("maxPrice");
  const [min, setMin] = useState(minP ? String(Number(minP) / SAR) : "");
  const [max, setMax] = useState(maxP ? String(Number(maxP) / SAR) : "");
  useEffect(() => setMin(minP ? String(Number(minP) / SAR) : ""), [minP]);
  useEffect(() => setMax(maxP ? String(Number(maxP) / SAR) : ""), [maxP]);
  const toCents = (v: string) => {
    const n = Number(v.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)).replace(/[^\d.]/g, ""));
    return v && n > 0 ? String(Math.round(n * SAR)) : null;
  };
  const applyPrice = () => set({ minPrice: toCents(min), maxPrice: toCents(max) });
  const sar = (n: number) => money(n * SAR, currency);
  const name = useId();

  return (
    <div className="filters">
      <fieldset className="filter-group">
        <legend>{t("filters.condition")} <GradeGuide /></legend>
        <label className="check-row">
          <input type="radio" name={`${name}-cond`} checked={!minCondition} onChange={() => set({ minCondition: null })} />
          <span>{t("filters.anyCondition")}</span>
        </label>
        {CONDITION_STEPS.map((c) => (
          <label key={c} className="check-row">
            <input type="radio" name={`${name}-cond`} checked={minCondition === c} onChange={() => set({ minCondition: c })} />
            <Grade c={c} />
            <span>{c === "new" ? t("cond.new") : t("cond.atLeast", { c: t(`cond.${c}`) })}</span>
          </label>
        ))}
      </fieldset>


      {facets.storages.length > 0 && (
        <fieldset className="filter-group">
          <legend>{t("filters.storage")}</legend>
          {facets.storages.map(({ gb, count }) => (
            <label key={gb} className="check-row">
              <input type="checkbox" checked={storages.includes(String(gb))} onChange={() => toggleStorage(String(gb))} />
              <span className="ltr">{gb >= 1024 ? `${gb / 1024} TB` : `${gb} GB`}</span>
              <span className="count">{count}</span>
            </label>
          ))}
        </fieldset>
      )}

      {(facets.colors?.length ?? 0) > 0 && (
        <fieldset className="filter-group">
          <legend>{t("filters.color")}</legend>
          {facets.colors!.map((c) => {
            const on = list(params.get("color")).some((x) => x.toLowerCase() === c.name.toLowerCase());
            const cur = list(params.get("color"));
            return (
              <label key={c.name} className="check-row">
                <input type="checkbox" checked={on} onChange={() => set({ color: (on ? cur.filter((x) => x.toLowerCase() !== c.name.toLowerCase()) : [...cur, c.name]).join(",") || null })} />
                {c.hex && <span className="swatch" style={{ background: c.hex }} aria-hidden="true" />}
                <span dir="auto">{(locale === "ar" && c.ar) || c.name}</span>
                <span className="count">{c.count}</span>
              </label>
            );
          })}
        </fieldset>
      )}

      <fieldset className="filter-group">
        <legend>{t("filters.price")}</legend>
        <div className="chip-grid">
          {PRICE_RANGES.map(([a, b]) => {
            const on = (minP ?? null) === (a ? String(a * SAR) : null) && (maxP ?? null) === (b ? String(b * SAR) : null);
            return (
              <button key={`${a}-${b}`} type="button" className="chip" aria-pressed={on} onClick={() => set(on ? { minPrice: null, maxPrice: null } : { minPrice: a ? String(a * SAR) : null, maxPrice: b ? String(b * SAR) : null })}>
                {a && b ? t("filters.range", { a: sar(a), b: sar(b) }) : a ? t("filters.over", { p: sar(a) }) : t("filters.under", { p: sar(b!) })}
              </button>
            );
          })}
        </div>
        <form className="price-inputs" onSubmit={(e) => { e.preventDefault(); applyPrice(); }}>
          <label>{t("filters.min")}<input className="input num" inputMode="numeric" dir="ltr" value={min} onChange={(e) => setMin(e.target.value)} onBlur={applyPrice} placeholder="0" /></label>
          <label>{t("filters.max")}<input className="input num" inputMode="numeric" dir="ltr" value={max} onChange={(e) => setMax(e.target.value)} onBlur={applyPrice} placeholder="10000" /></label>
        </form>
      </fieldset>

      <fieldset className="filter-group">
        <legend>{t("filters.delivery")}</legend>
        {(["any", "delivery", "pickup"] as const).map((d) => (
          <label key={d} className="check-row">
            <input type="radio" name={`${name}-del`} checked={delivery === d} onChange={() => set({ delivery: d === "any" ? null : d })} />
            <span>{d === "any" ? t("filters.any") : t(d === "delivery" ? "browse.delivery" : "browse.pickup")}</span>
          </label>
        ))}
      </fieldset>

      <fieldset className="filter-group">
        <legend>{t("warranty.title")}</legend>
        <label className="check-row">
          <input type="checkbox" checked={!!params.get("warranty")} onChange={(e) => set({ warranty: e.target.checked ? "1" : null })} />
          <span>{t("filters.warranty")}</span>
        </label>
      </fieldset>

      {activeCount > 0 && <button className="link clear" onClick={onClear}>{t("filters.clear")}</button>}
    </div>
  );
}
